import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, BookOpen, Share2, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@tabipla/ui";
import { Illustration } from "../components/Illustration";

export function LandingPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-indigo-50/50 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950/30">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500 shadow-lg">
              <MapPin className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-indigo-600">{t("app.name")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                {t("auth.login")}
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="bg-indigo-500 text-white hover:bg-indigo-600">
                {t("auth.register")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto max-w-5xl px-4 py-12 lg:py-20">
        <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
          {/* Text Content */}
          <div className="max-w-xl text-center lg:text-left">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
              {t("landing.hero.title")}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground">{t("landing.hero.subtitle")}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
              <Link to="/register">
                <Button
                  size="lg"
                  className="w-full bg-indigo-500 text-white shadow-lg hover:bg-indigo-600 sm:w-auto"
                >
                  {t("landing.cta")}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Illustration - hidden on mobile, shown from sm breakpoint */}
          <div className="hidden shrink-0 sm:block">
            <Illustration
              name="location-search"
              alt={t("landing.hero.title")}
              className="h-64 w-auto sm:h-72 lg:h-80"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
              {t("landing.featuresTitle", "Mekke でできること")}
            </h2>
          </div>

          <div className="mt-12 space-y-16 lg:space-y-20">
            {/* Feature 1: Record */}
            <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="shrink-0 lg:order-2">
                <Illustration
                  name="location-search"
                  alt={t("landing.features.record.title")}
                  className="h-48 w-auto sm:h-56 lg:h-64"
                />
              </div>
              <div className="max-w-lg text-center lg:order-1 lg:text-left">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-indigo-500/10 px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400">
                  <MapPin className="h-4 w-4" />
                  {t("landing.features.record.badge", "スポット記録")}
                </div>
                <h3 className="mb-4 text-xl font-bold sm:text-2xl">
                  {t("landing.features.record.title")}
                </h3>
                <p className="text-muted-foreground">{t("landing.features.record.description")}</p>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-indigo-500">✓</span>
                    {t("landing.features.record.point1", "写真と一緒に記録")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-indigo-500">✓</span>
                    {t("landing.features.record.point2", "カテゴリで整理")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-indigo-500">✓</span>
                    {t("landing.features.record.point3", "メモを追加")}
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 2: Organize */}
            <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="shrink-0">
                <Illustration
                  name="personal-notebook"
                  alt={t("landing.features.organize.title")}
                  className="h-48 w-auto sm:h-56 lg:h-64"
                />
              </div>
              <div className="max-w-lg text-center lg:text-left">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-green-500/10 px-4 py-2 text-sm font-medium text-green-600 dark:text-green-400">
                  <BookOpen className="h-4 w-4" />
                  {t("landing.features.organize.badge", "冊子作成")}
                </div>
                <h3 className="mb-4 text-xl font-bold sm:text-2xl">
                  {t("landing.features.organize.title")}
                </h3>
                <p className="text-muted-foreground">
                  {t("landing.features.organize.description")}
                </p>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-green-500">✓</span>
                    {t("landing.features.organize.point1", "テーマ別にまとめる")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-green-500">✓</span>
                    {t("landing.features.organize.point2", "ドラッグで並び替え")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-green-500">✓</span>
                    {t("landing.features.organize.point3", "複数の冊子を作成")}
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 3: Share */}
            <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="shrink-0 lg:order-2">
                <Illustration
                  name="share-link"
                  alt={t("landing.features.share.title")}
                  className="h-48 w-auto sm:h-56 lg:h-64"
                />
              </div>
              <div className="max-w-lg text-center lg:order-1 lg:text-left">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-4 py-2 text-sm font-medium text-amber-600 dark:text-amber-400">
                  <Share2 className="h-4 w-4" />
                  {t("landing.features.share.badge", "シェア")}
                </div>
                <h3 className="mb-4 text-xl font-bold sm:text-2xl">
                  {t("landing.features.share.title")}
                </h3>
                <p className="text-muted-foreground">{t("landing.features.share.description")}</p>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-amber-500">✓</span>
                    {t("landing.features.share.point1", "リンクでシェア")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-amber-500">✓</span>
                    {t("landing.features.share.point2", "公開範囲を設定")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-amber-500">✓</span>
                    {t("landing.features.share.point3", "友達の冊子を発見")}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works - 全幅背景 */}
      <section className="py-16" style={{ backgroundColor: "#4f46e5" }}>
        <div className="container mx-auto px-4">
          <div className="text-center text-white">
            <h2 className="text-3xl font-bold">
              {t("landing.howItWorksTitle", "使い方はかんたん")}
            </h2>
            <p className="mt-4 text-indigo-100">
              {t("landing.howItWorksSubtitle", "3ステップでスポット管理がスムーズに")}
            </p>
          </div>

          <div className="mt-12 grid gap-8 text-white sm:grid-cols-3">
            {[
              {
                step: "1",
                title: t("landing.step1Title", "スポットを記録"),
                description: t("landing.step1Description", "写真と一緒にお気に入りスポットを記録"),
              },
              {
                step: "2",
                title: t("landing.step2Title", "冊子にまとめる"),
                description: t("landing.step2Description", "テーマごとにスポットを整理"),
              },
              {
                step: "3",
                title: t("landing.step3Title", "シェアする"),
                description: t("landing.step3Description", "リンクで友達や家族と共有"),
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/20 text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="mt-4 text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm text-indigo-100">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center">
            <Illustration
              name="exploring"
              alt={t("landing.ctaTitle")}
              className="mb-8 h-48 w-auto"
            />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
              {t("landing.ctaTitle", "今すぐ始めよう")}
            </h2>
            <p className="mt-4 text-muted-foreground">
              {t("landing.ctaSubtitle", "登録かんたん、ずっと無料")}
            </p>
            <div className="mt-8">
              <Link to="/register">
                <Button
                  size="lg"
                  className="bg-indigo-500 text-white shadow-lg hover:bg-indigo-600"
                >
                  {t("landing.cta")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8">
        <div className="border-t pt-8">
          <p className="text-center text-sm text-muted-foreground">
            © {new Date().getFullYear()} Mekke. {t("landing.footer", "見つけた、をシェア。")}
          </p>
        </div>
      </footer>
    </div>
  );
}
